export function isApiSuccessStatus(status) {
  return String(status || "").toLowerCase() === "success";
}

export function normalizeChatSendApiResponse(res) {
  if (res == null) return res;
  if (typeof res !== "object") return res;
  if (res.backgroundProcessing) return res;
  if (isApiSuccessStatus(res.status)) return res;
  if (res.count != null || res.studentCount != null) {
    return { ...res, status: "success" };
  }
  return res;
}

function firstFinite(...values) {
  for (const value of values) {
    if (value === null || value === undefined || value === "") continue;
    const num = Number(value);
    if (Number.isFinite(num)) return num;
  }
  return undefined;
}

/**
 * Send endpoints return different shapes depending on whether the request
 * was handled directly or queued in the background. Some responses only
 * contain the queued payload (contacts/messages) and no count fields.
 * Derive a usable count from those payloads instead of falling back to 0.
 */
export function extractChatSendStats(res) {
  const normalized = normalizeChatSendApiResponse(res);
  if (!normalized || typeof normalized !== "object") {
    return { count: undefined, studentCount: undefined };
  }

  const data = normalized.data;
  const payload =
    data && !Array.isArray(data) && data.data != null ? data.data : data;
  const rootPayload =
    normalized.data && Array.isArray(normalized.data)
      ? normalized.data
      : payload;

  let count = firstFinite(
    normalized.count,
    normalized.messageCount,
    payload && payload.count,
    payload && payload.messageCount,
    payload && payload.messagesCount
  );
  let studentCount = firstFinite(
    normalized.studentCount,
    payload && payload.studentCount,
    payload && payload.studentsCount
  );

  const contacts = payload && payload.contacts;
  if (count === undefined && typeof contacts === "string") {
    const list = contacts
      .split(",")
      .map((entry) => entry.trim())
      .filter(Boolean);
    if (list.length) count = list.length;
  }
  if (count === undefined && Array.isArray(contacts)) {
    count = contacts.length;
  }
  if (count === undefined && payload && Array.isArray(payload.messages)) {
    count = payload.messages.length;
  }
  if (count === undefined && Array.isArray(rootPayload)) {
    count = rootPayload.length;
  }
  if (count === undefined && payload && Array.isArray(payload)) {
    count = payload.length;
  }
  if (
    studentCount === undefined &&
    payload &&
    Array.isArray(payload.students)
  ) {
    studentCount = payload.students.length;
  }

  return { count, studentCount };
}

export function isChatSendOk(res) {
  const normalized = normalizeChatSendApiResponse(res);
  if (!normalized) return false;
  if (normalized === true) return true;
  if (normalized.backgroundProcessing) return true;
  if (isApiSuccessStatus(normalized.status)) return true;
  return normalized.count != null || normalized.studentCount != null;
}

export function formatChatBulkSendMessage(res, fallbackMsg, t = (s) => s) {
  const payload = normalizeChatSendApiResponse(res);
  if (payload && payload.backgroundProcessing) {
    return (
      fallbackMsg ||
      t(
        "Your request is being processed in the background. Check Outbox for progress."
      )
    );
  }
  if (payload && typeof payload === "object" && isChatSendOk(payload)) {
    const { count, studentCount } = extractChatSendStats(payload);
    const messageLabel = count === 1 ? t("message") : t("messages");
    const studentLabel =
      studentCount === 1 ? t("student") : t("students");
    if (
      Number.isFinite(studentCount) &&
      Number.isFinite(count) &&
      (studentCount > 0 || count > 0)
    ) {
      return `${studentCount} ${studentLabel} (${count} ${messageLabel}) ${t(
        "queued. Processing in the background."
      )}`;
    }
    if (Number.isFinite(studentCount) && studentCount > 0) {
      return `${studentCount} ${studentLabel} ${t(
        "queued. Processing in the background."
      )}`;
    }
    if (Number.isFinite(count) && count > 0) {
      return `${count} ${messageLabel} ${t(
        "queued. Processing in the background."
      )}`;
    }
  }
  return (
    fallbackMsg ||
    t("Request submitted successfully. Processing in the background.")
  );
}
